import {
  Card,
  Grid,
  Stack,
  TextField,
  FormControlLabel,
  Button,
  Checkbox,
  Avatar,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import AutoComplete from '../../../components/AutoComplete';
import { useAppDispatch, useAppSelector } from '../../../store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  CreatePatchPropertySubCategory,
  PropertySubCategory as IPropertySubCategory,
  PropertyFacilitySubCategoryMapper,
  PropertyFeatureSubCategoryMapper,
} from '../../../store/propertySubCategory/api/interface';
import { useFormik } from 'formik';
import {
  createPropertySubCategory,
  getPropertySubCategory,
  patchPropertySubCategory,
} from '../../../store/propertySubCategory/api';
import { setStatus } from '../../../store/propertySubCategory/propertySubCategorySlice';
import { useEffect, useState } from 'react';
import { decryptString, getAssetURl } from '../../../utils/url';
import Loader from '../../../components/Loader';
import { omit, uniqBy } from 'lodash';
import {
  setStatus as selectPropertySubCategorySetStatus,
  reset as resetSelectPropertySubCategoryReset,
} from '../../../store/selectPropertySubCategory/selectPropertySubCategorySlice';
import propertySubCategorySchema from '../../../validation/propertySubCategorySchema';
import { NumericFormatCustom } from '../../../components/NumericCustomFormatter';
import {
  findSelectPropertyCategory,
  searchSelectPropertyCategory,
} from '../../../store/selectPropertyCategory/api';
import {
  setSkip as setSkipPropertyCategory,
  reset as resetPropertyCategory,
} from '../../../store/selectPropertyCategory/selectPropertyCategorySlice';
import { DocumentType } from '../../../enum';
import { uploadAssets } from '../../../utils/uploadAssets';
import { RiUploadCloud2Fill } from 'react-icons/ri';
import { PropertyFacility } from '../../../store/propertyFacility/api/interface';
import { PropertyFeature } from '../../../store/propertyFeature/api/interface';
import {
  findSelectPropertyFacility,
  searchSelectPropertyFacility,
} from '../../../store/selectPropertyFacility/api';
import {
  setSkip as selectPropertyFacilitySetStatus,
  reset as resetSelectPropertyFacilityReset,
  setStatus as setSelectPropertyFacilityStatus,
} from '../../../store/selectPropertyFacility/selectPropertyFacilitySlice';
import {
  findSelectPropertyFeature,
  searchSelectPropertyFeature,
} from '../../../store/selectPropertyFeature/api';
import {
  setSkip as selectPropertyFeatureSetStatus,
  reset as resetSelectPropertyFeatureReset,
  setStatus as setSelectPropertyFeatureStatus,
} from '../../../store/selectPropertyFeature/selectPropertyFeatureSlice';
import MultipleAutoComplete from '../../../components/MultipleAutoComplete';

const PropertySubCategoryForm = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const params: any = useParams();
  const location = useLocation();
  const state = location.state as IPropertySubCategory; // Type Casting, then you can get the params passed via router

  const isAddMode = !params?.id;
  const { isLoading } = useAppSelector((state) => state.propertySubCategory);
  const { accessToken } = useAppSelector((state) => state.auth);
  const [response, setResponse] = useState<any>();
  const [fileName, setFileName] = useState<string>('');

  const [propertyFacilities, setPropertyFacilities] = useState<
    PropertyFacility[]
  >([]);
  const [propertyFacilityIdsKey, setPropertyFacilityIdsKey] = useState<
    string | undefined
  >();

  const [propertyFeatures, setPropertyFeatures] = useState<PropertyFeature[]>(
    []
  );
  const [propertyFeatureIdsKey, setPropertyFeatureIdsKey] = useState<
    string | undefined
  >();

  const {
    handleSubmit,
    values,
    setValues,
    handleChange,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
  } = useFormik<CreatePatchPropertySubCategory>({
    initialValues: {
      id: '',
      labelEn: '',
      labelAr: '',
      icon: '',
      position: '',
      propertyCategoryId: '',
      propertyFeatureIds: [],
      propertyFacilityIds: [],
      activeState: true,
    },
    validationSchema: propertySubCategorySchema,
    onSubmit: async (values) => {
      try {
        if (isAddMode) {
          const reqBody = omit(values, ['id']);
          await dispatch(createPropertySubCategory(reqBody)).unwrap();
        }
        if (!isAddMode) {
          const reqBody: any = values;
          delete reqBody.createdAt;
          delete reqBody.createdBy;
          delete reqBody.updatedAt;
          delete reqBody.updatedBy;
          delete reqBody.property_category;
          delete reqBody.propertyFacilitySubCategoryMapper;
          delete reqBody.propertyFeatureSubCategoryMapper;
          await dispatch(patchPropertySubCategory(reqBody)).unwrap();
        }
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
        dispatch(resetSelectPropertySubCategoryReset());
        dispatch(selectPropertySubCategorySetStatus({ status: 'idle' }));
        navigation('/property-sub-category');
      } catch (error: any) {}
    },
  });

  useEffect(() => {
    const fetchById = async () => {
      if (isAddMode) return;

      if (state) {
        const propertyFacilityIds =
          state.propertyFacilitySubCategoryMapper?.map(
            (item) => item.propertyFacilityId ?? ''
          ) || [];
        const propertyFeatureIds =
          state.propertyFeatureSubCategoryMapper?.map(
            (item) => item.propertyFeatureId ?? ''
          ) || [];
        // setValues(state);
        setValues({
          ...state,
          propertyFeatureIds: propertyFeatureIds,
          propertyFacilityIds: propertyFacilityIds,
        });
        setPropertyFacilities(
          state.propertyFacilitySubCategoryMapper?.map(
            (propertyFacility) => propertyFacility.property_facility
          )
        );
        setPropertyFacilityIdsKey(
          `pfaids-${state?.propertyFacilitySubCategoryMapper?.length}`
        );
        setPropertyFeatures(
          state.propertyFeatureSubCategoryMapper?.map(
            (propertyFacility) => propertyFacility.property_feature
          )
        );
        setPropertyFeatureIdsKey(
          `pfeids-${state?.propertyFeatureSubCategoryMapper?.length}`
        );
        setResponse(state);
      } else {
        try {
          const decodeParam = decryptString(params.id);
          const record = await dispatch(
            getPropertySubCategory({ id: decodeParam })
          ).unwrap();
          // setValues(record);
          const propertyFacilityIds =
            record.propertyFacilitySubCategoryMapper?.map(
              (item: PropertyFacilitySubCategoryMapper) =>
                item.propertyFacilityId ?? ''
            ) || [];
          const propertyFeatureIds =
            record.propertyFeatureSubCategoryMapper?.map(
              (item: PropertyFeatureSubCategoryMapper) =>
                item.propertyFeatureId ?? ''
            ) || [];
          // setValues(state);
          setValues({
            ...record,
            propertyFeatureIds: propertyFeatureIds,
            propertyFacilityIds: propertyFacilityIds,
          });
          setPropertyFacilities(
            record.propertyFacilitySubCategoryMapper?.map(
              (propertyFacility: PropertyFacilitySubCategoryMapper) =>
                propertyFacility.property_facility
            )
          );
          setPropertyFacilityIdsKey(
            `pfaids-${record?.propertyFacilitySubCategoryMapper?.length}`
          );
          setPropertyFeatures(
            record.propertyFeatureSubCategoryMapper?.map(
              (propertyFacility: PropertyFeatureSubCategoryMapper) =>
                propertyFacility.property_feature
            )
          );
          setPropertyFeatureIdsKey(
            `pfeids-${record?.propertyFeatureSubCategoryMapper?.length}`
          );
          setResponse(record);
        } catch (error: any) {
          navigation('/property-sub-category');
        }
      }
    };
    fetchById();
  }, [dispatch, isAddMode, navigation, params?.id, setValues, state]);

  // Property Category Data
  const {
    data: propertyCategoryData,
    isLoading: isPropertyCategoryLoading,
    skip: propertyCategorySkip,
    hasMore: propertyCategoryHasMore,
    status: propertyCategoryStatus,
  } = useAppSelector((state) => state.selectPropertyCategory);

  useEffect(() => {
    if (propertyCategoryStatus === 'idle') {
      dispatch(
        findSelectPropertyCategory({
          limit: 10,
          skip: propertyCategorySkip,
          activeOnly: true,
        })
      );
    }
  }, [propertyCategorySkip, propertyCategoryStatus]);

  const onScrollFetchPropertyCategory = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      propertyCategoryHasMore
    ) {
      dispatch(setSkipPropertyCategory({ skip: propertyCategorySkip + 10 }));
      dispatch(
        findSelectPropertyCategory({
          limit: 10,
          skip: propertyCategorySkip + 10,
          ...(search && { search: search }),
          activeOnly: true,
        })
      );
    }
  };

  const searchPropertyCategory = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetPropertyCategory());
      return;
    }
    const params = {
      activeOnly: true,
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipPropertyCategory({ skip: 0 }));
    dispatch(searchSelectPropertyCategory(params));
  };

  const uniquePropertyCategoryData = uniqBy(
    [
      ...(response?.property_category ? [response?.property_category] : []),
      ...propertyCategoryData,
    ],
    'id'
  );

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: string,
    field: string
  ) => {
    if (event.target.files?.[0]) {
      const uploadResult: any = await uploadAssets(
        event.target.files?.[0],
        documentType,
        accessToken
      );
      if (uploadResult?.data?.id) {
        setFieldValue(field, uploadResult?.data?.keypath);
        setFileName(event.target.files?.[0]?.name);
      }
    }
  };

  // Property Facility
  const {
    data: propertyFacilityData,
    isLoading: isPropertyFacilityLoading,
    skip: skipPropertyFacility,
    hasMore: hasMorePropertyFacility,
    status: propertyFacilityStatus,
  } = useAppSelector((state) => state.selectPropertyFacility);

  useEffect(() => {
    if (propertyFacilityStatus === 'idle') {
      dispatch(
        findSelectPropertyFacility({
          limit: 10,
          skip: skipPropertyFacility,
          activeOnly: true,
        })
      );
    }
  }, [propertyFacilityStatus, skipPropertyFacility]);

  const onScrollFetchPropertyFacility = (
    event: React.UIEvent<HTMLElement, UIEvent>
  ) => {
    if (
      event.currentTarget.scrollTop + 300 >= event.currentTarget.scrollHeight &&
      hasMorePropertyFacility
    ) {
      dispatch(
        selectPropertyFacilitySetStatus({ skip: skipPropertyFacility + 10 })
      );
      dispatch(setSelectPropertyFacilityStatus({ status: 'idle' }));
    }
  };

  const searchPropertyFacility = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      dispatch(resetSelectPropertyFacilityReset());
      return;
    }
    const params = {
      activeOnly: true,
      ...(e.target.value && { limit: 50 }),
      ...(e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(searchSelectPropertyFacility(params));
  };

  const uniquePropertyFacilityData = uniqBy(
    [
      ...(propertyFacilities?.length > 0 ? [...propertyFacilities] : []),
      ...propertyFacilityData,
    ],
    'id'
  );

  // Property Feature
  const {
    data: propertyFeatureData,
    isLoading: isPropertyFeatureLoading,
    skip: skipPropertyFeature,
    hasMore: hasMorePropertyFeature,
    status: propertyFeatureStatus,
  } = useAppSelector((state) => state.selectPropertyFeature);

  useEffect(() => {
    if (propertyFeatureStatus === 'idle') {
      dispatch(
        findSelectPropertyFeature({
          limit: 10,
          skip: skipPropertyFeature,
          activeOnly: true,
        })
      );
    }
  }, [propertyFeatureStatus, skipPropertyFeature]);

  const onScrollFetchPropertyFeature = (
    event: React.UIEvent<HTMLElement, UIEvent>
  ) => {
    if (
      event.currentTarget.scrollTop + 300 >= event.currentTarget.scrollHeight &&
      hasMorePropertyFeature
    ) {
      dispatch(
        selectPropertyFeatureSetStatus({ skip: skipPropertyFeature + 10 })
      );
      dispatch(setSelectPropertyFeatureStatus({ status: 'idle' }));
    }
  };

  const searchPropertyFeature = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      dispatch(resetSelectPropertyFeatureReset());
      return;
    }
    const params = {
      activeOnly: true,
      ...(e.target.value && { limit: 50 }),
      ...(e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(searchSelectPropertyFeature(params));
  };

  const uniquePropertyFeatureData = uniqBy(
    [
      ...(propertyFeatures?.length > 0 ? [...propertyFeatures] : []),
      ...propertyFeatureData,
    ],
    'id'
  );

  return (
    <Grid
      container
      spacing={3}
      mt={1.5}
    >
      <Grid
        item
        xs={12}
        md={12}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <form onSubmit={handleSubmit}>
            <Card sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: {
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(2, 1fr)',
                  },
                }}
              >
                <TextField
                  fullWidth
                  label={'Label English'}
                  name='labelEn'
                  id='labelEn'
                  onChange={handleChange}
                  value={values.labelEn}
                  error={Boolean(errors.labelEn && touched.labelEn)}
                  helperText={
                    Boolean(errors.labelEn && touched.labelEn) && errors.labelEn
                  }
                />
                <TextField
                  fullWidth
                  label={'Label Arabic'}
                  name='labelAr'
                  id='labelAr'
                  onChange={handleChange}
                  value={values.labelAr}
                  error={Boolean(errors.labelAr && touched.labelAr)}
                  helperText={
                    Boolean(errors.labelAr && touched.labelAr) && errors.labelAr
                  }
                />

                <AutoComplete
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                    } else if (newValue) {
                      setFieldValue('propertyCategoryId', newValue.id);
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (!newInputValue) {
                      setFieldValue('propertyCategoryId', '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>{option.labelEn}</li>
                  )}
                  getOptionLabel={(option) => option.labelEn}
                  error={Boolean(
                    touched.propertyCategoryId && errors.propertyCategoryId
                  )}
                  helperText={
                    Boolean(
                      touched.propertyCategoryId && errors.propertyCategoryId
                    )
                      ? errors.propertyCategoryId
                      : ''
                  }
                  fetchFunction={onScrollFetchPropertyCategory}
                  label={'Select Property Category'}
                  loading={isPropertyCategoryLoading}
                  options={uniquePropertyCategoryData}
                  searchFunction={searchPropertyCategory}
                  value={
                    uniquePropertyCategoryData?.filter(
                      (country) => country.id === values.propertyCategoryId
                    )?.[0] || null
                  }
                />

                <Grid
                  container
                  spacing={2}
                >
                  <Grid
                    item
                    xs={values?.icon ? 8.5 : 12}
                    sm={values?.icon ? 8.5 : 12}
                  >
                    {/* <TextField
                      type='file'
                      fullWidth
                      name='icon'
                      id='icon'
                      onChange={(e) =>
                        handleFileChange(
                          e as React.ChangeEvent<HTMLInputElement>,
                          DocumentType.PropertySubCategory,
                          'icon'
                        )
                      }
                      inputProps={{ multiple: false, accept: 'image/*' }}
                      InputLabelProps={{ shrink: true }}
                      variant='outlined'
                      label={'Icon'}
                      error={Boolean(errors.icon && touched.icon)}
                      helperText={
                        Boolean(errors.icon && touched.icon) && errors.icon
                      }
                    /> */}
                    <TextField
                      fullWidth
                      variant='outlined'
                      InputLabelProps={{ shrink: true }}
                      label='Icon'
                      placeholder='Select Icon'
                      value={fileName}
                      error={Boolean(errors.icon && touched.icon)}
                      helperText={
                        Boolean(errors.icon && touched.icon) && errors.icon
                      }
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position='end'>
                            <label htmlFor='icon'>
                              <IconButton component='span'>
                                <RiUploadCloud2Fill />
                              </IconButton>
                            </label>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <input
                      id='icon'
                      name='icon'
                      type='file'
                      style={{ display: 'none' }}
                      onChange={(e) =>
                        handleFileChange(
                          e as React.ChangeEvent<HTMLInputElement>,
                          DocumentType.PropertySubCategory,
                          'icon'
                        )
                      }
                      accept='image/*'
                    />
                  </Grid>
                  {values?.icon && (
                    <Grid
                      item
                      xs={3.5}
                      sm={3.5}
                    >
                      <Avatar
                        imgProps={{ crossOrigin: 'anonymous' }}
                        src={getAssetURl(values?.icon)}
                        sx={{
                          borderRadius: 'unset',
                          m: 1,
                          objectFit: 'contain',
                          '& img': { objectFit: 'contain' },
                          width: { xs: '75px', sm: '100px' },
                          height: 50,
                        }}
                      />
                    </Grid>
                  )}
                </Grid>

                <TextField
                  fullWidth
                  label={'Position'}
                  name='position'
                  id='position'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.position}
                  error={Boolean(errors.position && touched.position)}
                  helperText={
                    Boolean(errors.position && touched.position) &&
                    errors?.position
                  }
                />
              </Box>

              <Box py={2}>
                <MultipleAutoComplete
                  key={propertyFacilityIdsKey}
                  onChange={(event, newValue, reason) => {
                    if (reason === 'removeOption') {
                      setFieldValue(
                        'propertyFacilityIds',
                        newValue?.map((propertyFacility) => propertyFacility.id)
                      );
                      setPropertyFacilities((propertyFacilityInfo) =>
                        propertyFacilityInfo.filter(
                          (propertyFacility) =>
                            propertyFacility?.id === newValue[0]?.id
                        )
                      );
                    }
                    if (typeof newValue === 'string') {
                    } else if (Array.isArray(newValue)) {
                      setFieldValue(
                        'propertyFacilityIds',
                        newValue?.map(
                          (propertyFacility) => propertyFacility?.id
                        )
                      );
                      setPropertyFacilities((curr) =>
                        uniqBy([...curr, ...newValue], 'id')
                      );
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (!newInputValue) {
                      setFieldValue('propertyFacilityIds', []);
                    }
                  }}
                  renderOption={(props, option: PropertyFacility) => (
                    <li {...props}>{option.labelEn}</li>
                  )}
                  getOptionLabel={(option: PropertyFacility) => option.labelEn}
                  fetchFunction={onScrollFetchPropertyFacility}
                  label={'Select Property Facilities'}
                  onFocus={() =>
                    dispatch(
                      setSelectPropertyFacilityStatus({ status: 'idle' })
                    )
                  }
                  loading={isPropertyFacilityLoading}
                  options={uniquePropertyFacilityData}
                  searchFunction={searchPropertyFacility}
                  value={uniquePropertyFacilityData.filter((item) =>
                    values.propertyFacilityIds.includes(item.id)
                  )}
                  limitTags={4}
                  error={Boolean(
                    touched.propertyFacilityIds && errors.propertyFacilityIds
                  )}
                  helperText={
                    Boolean(
                      touched.propertyFacilityIds && errors.propertyFacilityIds
                    )
                      ? errors?.propertyFacilityIds
                      : ''
                  }
                />
              </Box>

              <Box pb={2}>
                <MultipleAutoComplete
                  key={propertyFeatureIdsKey}
                  onChange={(event, newValue, reason) => {
                    if (reason === 'removeOption') {
                      setFieldValue(
                        'propertyFeatureIds',
                        newValue?.map((propertyFeature) => propertyFeature.id)
                      );
                      setPropertyFeatures((propertyFeatureInfo) =>
                        propertyFeatureInfo.filter(
                          (propertyFeature) =>
                            propertyFeature?.id === newValue[0]?.id
                        )
                      );
                    }
                    if (typeof newValue === 'string') {
                    } else if (Array.isArray(newValue)) {
                      setFieldValue(
                        'propertyFeatureIds',
                        newValue?.map((propertyFeature) => propertyFeature?.id)
                      );
                      setPropertyFeatures((curr) =>
                        uniqBy([...curr, ...newValue], 'id')
                      );
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (!newInputValue) {
                      setFieldValue('propertyFeatureIds', []);
                    }
                  }}
                  renderOption={(props, option: PropertyFeature) => (
                    <li {...props}>{option.labelEn}</li>
                  )}
                  getOptionLabel={(option: PropertyFeature) => option.labelEn}
                  fetchFunction={onScrollFetchPropertyFeature}
                  label={'Select Property Features'}
                  onFocus={() =>
                    dispatch(setSelectPropertyFeatureStatus({ status: 'idle' }))
                  }
                  loading={isPropertyFeatureLoading}
                  options={uniquePropertyFeatureData}
                  searchFunction={searchPropertyFeature}
                  value={uniquePropertyFeatureData.filter((item) =>
                    values.propertyFeatureIds.includes(item.id)
                  )}
                  limitTags={4}
                  error={Boolean(
                    touched.propertyFeatureIds && errors.propertyFeatureIds
                  )}
                  helperText={
                    Boolean(
                      touched.propertyFeatureIds && errors.propertyFeatureIds
                    )
                      ? errors?.propertyFeatureIds
                      : ''
                  }
                />
              </Box>

              <Grid
                container
                spacing={2}
                py={2}
              >
                <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        name='activeState'
                        onChange={(e) =>
                          setFieldValue('activeState', e.target.checked)
                        }
                        checked={values.activeState}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                      />
                    }
                    label={'Active Status'}
                  />
                </Grid>
              </Grid>

              <Stack
                alignItems='center'
                sx={{ mt: 3 }}
                direction='row'
                justifyContent='flex-end'
                gap={1}
              >
                <Button
                  variant='outlined'
                  onClick={() => navigation('/property-sub-category')}
                >
                  {'Cancel'}
                </Button>

                <LoadingButton
                  type='submit'
                  variant='contained'
                  loading={isSubmitting}
                >
                  {isAddMode
                    ? 'Add Property Sub Category'
                    : 'Edit Property Sub Category'}
                </LoadingButton>
              </Stack>
            </Card>
          </form>
        )}
      </Grid>
    </Grid>
  );
};

export default PropertySubCategoryForm;
